import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@mui/styles'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import clsx from 'clsx'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'

const useStyles = makeStyles((theme) => ({
  tooltip: {
    color: theme.black,
  },
  disabled: {
    opacity: '50%',
  },
  lockedContent: {
    opacity: '50%',
  },
  dropDownItem: ({ disabled, locked }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '16.5px 0px',
    margin: '0 23px',
    cursor: (disabled && !locked) ? 'default' : 'pointer',
    borderLeft: '3px solid transparent',
    borderBottom: theme.border,
    '&:hover': (!disabled || locked) && {
      background: '#f6f7fd',
      margin: 0,
      padding: '16.5px 23px',
      borderColor: theme.borderColor,
      borderLeftColor: theme.primary,
    },
    '& dropDownIcon': disabled && {
      fill: theme.orange,
    },
    '&:last-of-type': {
      borderBottom: 'none',
    },
  }),
  dropDownIcon: {
    marginRight: 15,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.lightBackground,
    padding: 4,
    borderRadius: 8,
    marginLeft: 'auto',
    marginRight: 0,
  },
  icon: {
    fontSize: theme.fontSizes.smedium,
    color: theme.primary,
    height: 12,
    width: 12,
  },
}))

const DropDownItem = (props) => {
  const {
    label,
    onClick = () => {},
    actionId,
    tooltip,
    icon,
    dataQsId,
    iconWidth = '16px',
    iconHeight = '18px',
    className,
    disabled,
    locked,
  } = props
  const classes = useStyles({ disabled, locked })
  const theme = useTheme()
  const isDisabled = !!disabled && !locked
  const handleKeyDown = (event) => {
    if (isDisabled || (event.key !== 'Enter' && event.key !== ' ')) return

    event.preventDefault()
    event.currentTarget.click()
  }

  let content = (
    <div
      className={clsx(classes.dropDownItem, className, 'dropDownItem')}
      role='menuitem'
      tabIndex={0}
      aria-disabled={isDisabled}
      onClick={!isDisabled ? onClick : undefined}
      onKeyDown={handleKeyDown}
      {...(actionId && { actionId })}
      {...(dataQsId && { 'data-qs-id': dataQsId })}
    >
      <div className={locked ? classes.lockedContent : undefined} style={{ display: 'flex', alignItems: 'center' }}>
        {icon && <SVGIcon svg={icon} width={iconWidth} height={iconHeight} className={classes.dropDownIcon} fill={disabled ? theme.disabledColor : undefined} />}
        <div>
          {label}
        </div>
      </div>
      {locked && (
        <div className={classes.iconContainer}>
          <RocketLaunchIcon className={classes.icon} />
        </div>
      )}
    </div>
  )
  if (tooltip) {
    content = (<RichTooltip
      content={(<div className={classes.tooltip}>{tooltip}</div>)}
      placement='bottom'
      width={300}>
      {content}
    </RichTooltip>)
  }

  return (
    // This wrapper sits between SplitButton's role='menu' container and the menuitem —
    // role='presentation' keeps it out of the accessibility tree so screen readers count
    // the items as direct menu children. Pure semantics, no style change.
    <div role='presentation' className={isDisabled ? classes.disabled : undefined}>
      {content}
    </div>
  )
}

DropDownItem.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  actionId: PropTypes.string,
  // `A || B` collapsed to A (plain node) — an icon is an SVG component or element
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  iconWidth: PropTypes.string,
  iconHeight: PropTypes.string,
  dataQsId: PropTypes.string,
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  locked: PropTypes.bool,
}

export default DropDownItem
